/**
 * 由背景色推断明暗调性（与 `@hs/ui/lib/text-on-background` 行为对齐，避免 canvas 依赖 @hs/ui）。
 * WCAG 2.1：比较黑字、白字与背景的对比度，取更高者对应的调性。
 */
export type BackgroundToneMode = 'light' | 'dark';

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

/** sRGB 0–255 -> 线性分量 */
function srgbToLinear(c: number) {
  const x = clamp01(c / 255);
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
}

function relativeLuminanceFromRgb(r: number, g: number, b: number) {
  const R = srgbToLinear(r);
  const G = srgbToLinear(g);
  const B = srgbToLinear(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function contrastRatio(l1: number, l2: number) {
  const L1 = Math.max(l1, l2);
  const L2 = Math.min(l1, l2);
  return (L1 + 0.05) / (L2 + 0.05);
}

function parseCssColorToRgb(css: string): [number, number, number] | null {
  const s = css.trim();
  if (!s) return null;
  const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1];
    if (h.length === 3) {
      const r = parseInt(h[0] + h[0], 16);
      const g = parseInt(h[1] + h[1], 16);
      const b = parseInt(h[2] + h[2], 16);
      return [r, g, b];
    }
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return [r, g, b];
  }
  const rgb = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }
  return null;
}

/**
 * 根据任意 CSS 颜色字符串，判断背景偏亮还是偏暗。
 */
export function getBackgroundToneFromCss(cssColor: string): BackgroundToneMode {
  const rgb = parseCssColorToRgb(cssColor);
  if (!rgb) return 'light';
  const L = relativeLuminanceFromRgb(rgb[0], rgb[1], rgb[2]);
  const ratioBlack = contrastRatio(L, 0);
  const ratioWhite = contrastRatio(1, L);
  return ratioBlack >= ratioWhite ? 'light' : 'dark';
}
